import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function DateFilter({
  value,
  onChange,
  customDateRange,
  onCustomDateRangeChange,
}) {
  const { t } = useLanguage();
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);

  const handleSelectChange = (newValue) => {
    if (newValue === "custom") {
      onChange("custom");
      // Open popover when custom is selected
      setTimeout(() => setIsCustomRangeOpen(true), 0);
    } else {
      setIsCustomRangeOpen(false);
      onChange(newValue);
    }
  };

  const handleCustomDateRangeSelect = (range) => {
    const newRange = range || { from: null, to: null };
    onCustomDateRangeChange(newRange);

    // If both dates are selected, close the popover
    if (newRange.from && newRange.to) {
      // Small delay to allow the calendar to update before closing
      setTimeout(() => {
        setIsCustomRangeOpen(false);
      }, 100);
    }
  };

  const handlePopoverOpenChange = (open) => {
    setIsCustomRangeOpen(open);
  };

  const getDisplayText = () => {
    if (value === "custom" && customDateRange?.from && customDateRange?.to) {
      return `${format(customDateRange.from, "MMM d")} - ${format(customDateRange.to, "MMM d, yyyy")}`;
    }

    switch (value) {
      case "today":
        return t("thisDate");
      case "week":
        return t("thisWeek");
      case "month":
        return t("thisMonth");
      case "year":
        return t("thisYear");
      case "custom":
        return customDateRange?.from
          ? format(customDateRange.from, "MMM d, yyyy")
          : t("customRange");
      default:
        return t("thisDate");
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
      <Select value={value} onValueChange={handleSelectChange}>
        <SelectTrigger className="w-full sm:w-[160px] bg-white">
          <Calendar className="h-4 w-4 mr-2 text-slate-400" />
          <SelectValue>{getDisplayText()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="today" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("thisDate")}</SelectItem>
          <SelectItem value="week" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("thisWeek")}</SelectItem>
          <SelectItem value="month" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("thisMonth")}</SelectItem>
          <SelectItem value="year" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("thisYear")}</SelectItem>
          <SelectItem value="custom" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("customRange")}</SelectItem>
        </SelectContent>
      </Select>

      {value === "custom" && (
        <Popover open={isCustomRangeOpen} onOpenChange={handlePopoverOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="gap-2 bg-white w-full sm:w-auto"
              onClick={() => setIsCustomRangeOpen(!isCustomRangeOpen)}
            >
              <Calendar className="h-4 w-4" />
              {customDateRange?.from && customDateRange?.to
                ? `${format(customDateRange.from, "MMM d")} - ${format(customDateRange.to, "MMM d, yyyy")}`
                : customDateRange?.from
                  ? format(customDateRange.from, "MMM d, yyyy")
                  : t("selectRange")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={customDateRange || { from: null, to: null }}
              onSelect={handleCustomDateRangeSelect}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}





